import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import './ModalService.css';

const ModalContext = createContext();
const ModalDepthContext = createContext(0);

// Modal hosts must stay above persistent app chrome. In particular, the
// TopBar's social control occupies the same top-right hit area as the modal X;
// sharing its stacking level lets mobile sticky-layer compositing route taps to
// the control underneath even while the X is visibly painted over it.
export const MODAL_BASE_Z_INDEX = 30000;
export const getModalZIndex = (stackIndex) => MODAL_BASE_Z_INDEX + stackIndex;

export const resolveModalLayout = (ModalComponent, options = {}) => (
  options.layout
  || ModalComponent?.modalLayout
  || ((ModalComponent?.displayName || ModalComponent?.name) === 'TimerModal' ? 'edge-to-edge' : 'default')
);

export const getModalContentClassName = (ModalComponent, options = {}) => (
  resolveModalLayout(ModalComponent, options) === 'edge-to-edge'
    ? 'modal-content timer-modal-host'
    : 'modal-content'
);

const ScopedModalBody = ({ ModalComponent, props, close, canClose }) => {
  const modal = useContext(ModalContext);
  const depth = useContext(ModalDepthContext);
  const modalService = React.useMemo(() => ({
    ...modal,
    onBeforeClose: (handler) => modal.onBeforeClose(handler, depth),
    clearBeforeClose: () => modal.clearBeforeClose(depth),
  }), [modal, depth]);
  return <ModalComponent {...props} modalService={modalService} close={close} canClose={canClose} />;
};

const ModalWrapper = ({ ModalComponent, props, options, zIndex, close, tryClose, canClose, isNested, depth }) => {
  const handleBackgroundClick = async (e) => {
    // Disabled closing on background click to prevent accidental closes
    // if (e.target.className === 'modal' && canClose) {
    //   await tryClose();
    // }
  };
  return (
    <div className="modal" onClick={handleBackgroundClick} style={{ zIndex }}>
      <div
        className={getModalContentClassName(ModalComponent, options)}
        data-modal-layout={resolveModalLayout(ModalComponent, options)}
      >
        <ModalDepthContext.Provider value={depth}>
          <ScopedModalBody ModalComponent={ModalComponent} props={props} close={close} canClose={canClose} />
        </ModalDepthContext.Provider>
      </div>
      {!props.hideCloseButton && (
        <button
          onClick={tryClose}
          className={`close-button ${isNested ? 'back-button' : ''} ${canClose ? '' : 'disabled'}`}
          aria-label={isNested ? 'Back' : 'Close'}
          disabled={!canClose}
        >
          {isNested ? '\u2190' : '\u00d7'}
        </button>
      )}
    </div>
  );
};

export const ModalProvider = ({ children }) => {
  const [modals, setModals] = useState([]);
  const [canClose, setCanClose] = useState(true);
  const beforeCloseHandlers = useRef(new Map());
  const queryClient = useRef(new QueryClient());
  const lockedBodyScrollY = useRef(0);
  const previousBodyStyles = useRef(null);

  const open = useCallback((ModalComponent, props, options = {}) => {
    // Ensure modals carry an explicit isUsingUnits flag (default false)
    const safeProps = Object.assign({}, props, { isUsingUnits: props && props.isUsingUnits === true });
    const id = Date.now() + Math.random();
    setModals((prev) => [
      ...prev,
      { ModalComponent, props: safeProps, options, id }
    ]);
  }, []);

  const updateProps = useCallback((newProps) => {
    setModals((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      const updatedLast = {
        ...last,
        props: { ...last.props, ...newProps }
      };
      
      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const close = useCallback(() => {
    setModals((prev) => {
      beforeCloseHandlers.current.delete(prev.length);
      return prev.slice(0, -1);
    });
  }, []);

  // Refs used by the back-button bridge so the popstate listener can stay
  // mounted exactly once for the app's lifetime and still see current state.
  const modalsRef = useRef(modals);
  useEffect(() => { modalsRef.current = modals; }, [modals]);

  const tryClose = useCallback(async () => {
    if (!canCloseRef.current) return false;
    const handler = beforeCloseHandlers.current.get(modalsRef.current.length);
    if (handler) {
      const shouldClose = await handler();
      if (!shouldClose) return false;
    }
    close();
    return true;
  }, [close]);
  const tryCloseRef = useRef(tryClose);
  const canCloseRef = useRef(canClose);
  useEffect(() => { tryCloseRef.current = tryClose; }, [tryClose]);
  useEffect(() => { canCloseRef.current = canClose; }, [canClose]);

  const onBeforeClose = useCallback((handler, depth) => {
    beforeCloseHandlers.current.set(depth, handler);
    return () => {
      if (beforeCloseHandlers.current.get(depth) === handler) {
        beforeCloseHandlers.current.delete(depth);
      }
    };
  }, []);

  const clearBeforeClose = useCallback((depth) => {
    beforeCloseHandlers.current.delete(depth);
  }, []);

  const disableClose = useCallback(() => setCanClose(false), []);
  const enableClose = useCallback(() => setCanClose(true), []);

  // Handle Escape key
  React.useEffect(() => {
    if (modals.length === 0) return;
    const handleEscape = async (e) => {
      if (e.key === 'Escape' && canClose) {
        await tryClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [modals, canClose, tryClose]);

  // Handle back button / Android back gesture.
  //
  // We keep one history entry per open modal so a single back press always
  // closes exactly one (the topmost). The previous implementation re-pushed
  // a history entry every time the modal stack changed, which meant opening
  // a second modal added an entry but closing it programmatically did NOT
  // remove one — so after stacking two modals the back gesture appeared to
  // do nothing because the user had to press it as many times as there had
  // been stack mutations.
  //
  // Strategy:
  //  - expectedDepthRef tracks how many history entries we've pushed.
  //  - When modals.length grows, push one entry per new modal.
  //  - When modals.length shrinks (programmatic close), call history.back()
  //    for each removed modal and suppress the resulting popstate so it
  //    doesn't close another modal in a feedback loop.
  //  - User-initiated popstate (real back press/gesture) closes one modal.
  const expectedDepthRef = useRef(0);
  const suppressPopRef = useRef(0);
  const popCloseInFlightRef = useRef(false);
  const queuedPopCountRef = useRef(0);

  useEffect(() => {
    const onPop = async () => {
      if (suppressPopRef.current > 0) {
        suppressPopRef.current -= 1;
        return;
      }
      if (modalsRef.current.length > 0) {
        if (popCloseInFlightRef.current) {
          queuedPopCountRef.current += 1;
          return;
        }
        popCloseInFlightRef.current = true;
        expectedDepthRef.current = Math.max(0, expectedDepthRef.current - 1);
        let closed = false;
        try {
          closed = canCloseRef.current ? await tryCloseRef.current() : false;
        } catch (error) {
          console.error('[ModalService] Close guard failed:', error);
        } finally {
          popCloseInFlightRef.current = false;
        }
        const queuedPops = queuedPopCountRef.current;
        queuedPopCountRef.current = 0;
        if (!closed) expectedDepthRef.current += 1;
        const restoreCount = queuedPops + (closed ? 0 : 1);
        for (let index = restoreCount - 1; index >= 0; index -= 1) {
          window.history.pushState({ modalDepth: expectedDepthRef.current - index }, '');
        }
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    const target = modals.length;
    while (expectedDepthRef.current < target) {
      expectedDepthRef.current += 1;
      window.history.pushState({ modalDepth: expectedDepthRef.current }, '');
    }
    while (expectedDepthRef.current > target) {
      expectedDepthRef.current -= 1;
      suppressPopRef.current += 1;
      window.history.back();
    }
  }, [modals.length]);

  // Handle body scroll locking
  React.useEffect(() => {
    if (modals.length > 0) {
      if (!document.body.classList.contains('modal-open')) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        lockedBodyScrollY.current = window.scrollY || window.pageYOffset || 0;
        previousBodyStyles.current = {
          overflow: document.body.style.overflow,
          paddingRight: document.body.style.paddingRight,
          position: document.body.style.position,
          top: document.body.style.top,
          left: document.body.style.left,
          right: document.body.style.right,
          width: document.body.style.width,
        };
        document.body.style.paddingRight = `${scrollbarWidth}px`;
        document.body.style.overflow = 'hidden';
        // iOS 14/15 ignore overflow:hidden for root scroll chaining. Freezing
        // the body preserves the underlying scroll position while modal content
        // owns all touch scrolling.
        document.body.style.position = 'fixed';
        document.body.style.top = `-${lockedBodyScrollY.current}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        document.body.classList.add('modal-open');
      }
    } else if (document.body.classList.contains('modal-open')) {
      const previous = previousBodyStyles.current || {};
      document.body.style.overflow = previous.overflow || '';
      document.body.style.paddingRight = previous.paddingRight || '';
      document.body.style.position = previous.position || '';
      document.body.style.top = previous.top || '';
      document.body.style.left = previous.left || '';
      document.body.style.right = previous.right || '';
      document.body.style.width = previous.width || '';
      document.body.classList.remove('modal-open');
      if (!/jsdom/i.test(window.navigator?.userAgent || '')) {
        window.scrollTo(0, lockedBodyScrollY.current);
      }
      previousBodyStyles.current = null;
    }
  }, [modals.length]);

  // Track visual viewport height for mobile keyboard awareness
  useEffect(() => {
    // isKeyboardOpen is only toggled by resize events so that URL-bar
    // show/hide during scroll can't flicker the threshold and cause jumps.
    let isKeyboardOpen = false;
    const style = document.documentElement.style;

    // Resize: keyboard opens/closes, URL bar changes, device rotation.
    // Updates all three CSS variables.
    const onResize = () => {
      const vv = window.visualViewport;
      const vh = vv ? vv.height : window.innerHeight;
      style.setProperty('--visual-viewport-height', `${vh}px`);

      const kbHeight = vv ? Math.max(0, window.innerHeight - vv.height) : 0;
      isKeyboardOpen = kbHeight > 50;
      document.body.dataset.keyboardOpen = isKeyboardOpen ? 'true' : 'false';
      style.setProperty('--keyboard-height', isKeyboardOpen ? `${Math.round(kbHeight)}px` : '0px');

      // Also sync scroll offset since a resize can shift the viewport
      style.setProperty('--vv-offset-top', isKeyboardOpen && vv ? `${Math.round(vv.offsetTop)}px` : '0px');
    };

    // Scroll: visual viewport moves within the layout viewport.
    // Synchronous (no rAF) so the transform update lands in the same
    // frame as the browser's own scroll paint — eliminates the one-frame
    // lag that caused direction-change jitter.
    const onScroll = () => {
      const vv = window.visualViewport;
      style.setProperty('--vv-offset-top', isKeyboardOpen && vv ? `${Math.round(vv.offsetTop)}px` : '0px');
    };

    // Initial apply
    onResize();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onResize);
      window.visualViewport.addEventListener('scroll', onScroll);
    }
    window.addEventListener('resize', onResize);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', onResize);
        window.visualViewport.removeEventListener('scroll', onScroll);
      }
      window.removeEventListener('resize', onResize);
      delete document.body.dataset.keyboardOpen;
    };
  }, []);

  return (
    <ModalContext.Provider value={{
      open,
      close,
      updateProps,
      disableClose,
      enableClose,
      onBeforeClose,
      clearBeforeClose,
      modals
    }}>
      <QueryClientProvider client={queryClient.current}>
        {children}
        {modals.map(({ ModalComponent, props, options, id }, idx) => (
          <ModalWrapper
            key={id || idx}
            ModalComponent={ModalComponent}
            props={props}
            options={options}
            zIndex={getModalZIndex(idx)}
            close={close}
            tryClose={tryClose}
            canClose={canClose}
            isNested={idx > 0}
            depth={idx + 1}
          />
        ))}
      </QueryClientProvider>
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const modal = useContext(ModalContext);
  const depth = useContext(ModalDepthContext);
  const onBeforeClose = useCallback((handler) => modal.onBeforeClose(handler, depth), [modal, depth]);
  const clearBeforeClose = useCallback(() => modal.clearBeforeClose(depth), [modal, depth]);
  return { ...modal, onBeforeClose, clearBeforeClose };
};